import React, { useContext, useEffect, useRef, useState } from 'react';
import { first, get } from 'lodash';
import styled from 'styled-components';

import addParseErrorsToCsv from 'src/presentation/components/pages/campaigns/add_parse_errors_to_csv';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { CampaignSectionRow } from './RecipientCollectionsLayout';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import FormContext from 'src/presentation/contexts/FormContext';
import { getCsvUri } from './helper';
import UploadStrokeIcon from '@ad-scripts/presentation/components/common/icons/stroke/upload-stroke';

const NoFileSelectedError = 'Please upload the Recipients CSV file.';

const UploadCsvButton = ({ attr, errors }) => {
  const { updateState } = useContext(FormContext);

  const [csvWithParseErrs, setCsvWithParseErrs] = useState<string | null>(null);
  const [fileError, setFileError] = useState('');

  const csvInputRef = useRef<any>();
  const [selectedFile, setSelectedFile] = useState();

  const handleFileSelection = event => {
    if (event.target?.files) {
      const { files } = event.target;
      const selectedFile = first(files);
      const selectedFileName = get(selectedFile, 'name');
      setSelectedFile(selectedFileName);
      updateState({ [attr]: selectedFile || null });
      setFileError('');
      setCsvWithParseErrs(null);
    }
  };

  useEffect(() => {
    if (errors.filter(err => err.attr === 'csvFile' && err.code === 'blank').length) {
      setFileError(NoFileSelectedError);
      return;
    }

    const greaterThanError = errors.find(err => err.code === 'greater_than');
    if (greaterThanError) {
      setFileError(`${greaterThanError.detail}.`);
      return;
    }

    if (!errors || errors.length === 0 || !csvInputRef || !hasCSVParseError(errors)) {
      setCsvWithParseErrs(null);
      setFileError('');
      return;
    }

    const files = get(csvInputRef, 'current.files');
    if (!files || files.length === 0) {
      setCsvWithParseErrs(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      if (e.target) {
        const originalCSV = e.target.result;
        if (originalCSV) {
          const csvWithErrors = addParseErrorsToCsv(errors, originalCSV);
          setCsvWithParseErrs(csvWithErrors);
        }
      }
    };
    reader.readAsText(files[0]);
  }, [errors]);

  const openFileDialog = () => {
    csvInputRef.current!.click();
  };

  return (
    <>
      <CampaignSectionRow>
        <Button buttonType={ButtonTypes.SECONDARY} onClick={openFileDialog}>
          <CSVFileInput ref={csvInputRef} attr="csvFile" type="file" onChange={handleFileSelection} accept=".csv" />
          <StyledUploadStrokeIcon /> Upload CSV
        </Button>
        <SelectedFile>{selectedFile}</SelectedFile>
      </CampaignSectionRow>
      {errors && <ErrorRow fileError={fileError} csvWithParseErrs={csvWithParseErrs} />}
    </>
  );
};

// "Please upload the Recipients CSV" />}
const ErrorRow = ({ fileError, csvWithParseErrs }: { fileError: string; csvWithParseErrs: string | null }) => {
  return (
    <>
      {fileError && <FileError error={fileError} />}
      {csvWithParseErrs && <DownloadCSVError csvWithParseErrs={csvWithParseErrs} />}
    </>
  );
};

const FileError = ({ error }) => (
  <CampaignSectionRow>
    <ErrorMessage>{error}</ErrorMessage>
  </CampaignSectionRow>
);

const DownloadCSVError = ({ csvWithParseErrs }) => (
  <CampaignSectionRow>
    <ErrorMessage>
      An error was found on the CSV file.{' '}
      <ExternalLink to={getCsvUri(csvWithParseErrs)} download label="Download the error log" /> and upload the file
      again.
    </ErrorMessage>
  </CampaignSectionRow>
);

const _hasErrorAttr = (errors, attr) => errors.filter(err => err.attr === attr).length > 0;

const hasCSVParseError = errors => _hasErrorAttr(errors, 'csvFileParseError');

const SelectedFile = styled.span``;

const CSVFileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.p`
  color: ${p => p.theme.colors.red400};
`;

const StyledUploadStrokeIcon = styled(UploadStrokeIcon)`
  width: 16px;
  height: 16px;
  fill: ${p => p.theme.colors.gray700};
  margin-right: 4px;
  position: relative;
  top: 2px;
`;

export default UploadCsvButton;
